import React from "react";
import { AlarmClockCheckIcon, CameraIcon, FolderCheckIcon, HardDriveIcon, MicroscopeIcon } from "lucide-react";
import clsx from "clsx";
import { fileMocks } from "./upload/uploadMocks";
import { remindersAndEvents } from "./calendars/mocks/calendarMocks";
import { DashboardEventsTable } from "@/components/feature/tables/dashboard_cards/DashboardEventsTable";
import { DashboardDriveTable } from "@/components/feature/tables/dashboard_cards/DashboardDriveTable";
import { DriveTableRecentItems } from "@/components/feature/tables/dashboard_cards/DriveTableRecentItems";
import { DashboardRecentUploads } from "./recentUploads";
import { useDemoAccess } from "@/hooks/use-demo-access";
import { SnapshotsTableRecentItems } from "./snapshots_card/snapshotsCard";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Link } from "react-router";

interface Action {
  title: string;
  description?: string;
  href: string;
  icon: React.ElementType;
  iconForeground: string;
  iconBackground: string;
  width: string;
  content?: React.ReactNode;
}

const ResearchFiles = () => (
  <div className="mt-4">
    <ul className="mt-4 space-y-4">
      {fileMocks.slice(0, 2).map(file => (
        <li key={file.id} className="rounded-md bg-white p-4 shadow-md">
          <Link className="font-semibold text-ui-sidebar-text hover:underline" to={`/ask/research/${file.id}`}>
            {file.name}
          </Link>
          <p className="mt-2 text-sm text-ui-paragraph-text">
            This document contains comprehensive research on the specified topic, including analysis, findings, and
            recommendations.
          </p>
          <p className="text-sm text-ui-paragraph-text">
            For further details, please review the full document available via the provided link.
          </p>
        </li>
      ))}
    </ul>
  </div>
);

export const HomeView = () => {
  const userData = useAuthenticatedUser();
  const { hasFeature } = useDemoAccess();

  const actions: Action[] = [
    ...(hasFeature("deadline_guardian")
      ? [
          {
            title: "Deadline Guardian",
            description: "Never Miss a Deadline.",
            href: "/calendars",
            icon: AlarmClockCheckIcon,
            iconForeground: "text-midnight-blue-700",
            iconBackground: "bg-ivory-mist-500",
            width: "col-span-12 sm:col-span-6",
            content: <DashboardEventsTable eventsAndReminders={remindersAndEvents} limit={5} />,
          },
        ]
      : []),
    {
      title: "My Folders",
      description: hasFeature("deadline_guardian")
        ? "Manage, search and action your files & folders"
        : "Access your files and folders, and manage your content.",
      href: "/drive",
      icon: HardDriveIcon,
      iconForeground: "text-ui-card-icon-color",
      iconBackground: "bg-ivory-mist-400",
      width: "col-span-12 sm:col-span-6",
      content: hasFeature("deadline_guardian") ? <DashboardDriveTable /> : <DriveTableRecentItems />,
    },
    {
      title: "Recent uploads",
      description: "Access the latest files that have been uploaded to My Folders.",
      href: "/jobsmonitor",
      icon: FolderCheckIcon,
      iconForeground: "text-ui-card-icon-color",
      iconBackground: "bg-desert-sand-200",
      width: "col-span-12 sm:col-span-6",
      content: <DashboardRecentUploads />,
    },
    ...(hasFeature("deadline_guardian")
      ? [
          {
            title: "Research",
            href: "/settings",
            description: "Recent research",
            icon: MicroscopeIcon,
            iconForeground: "text-ui-card-icon-color",
            iconBackground: "bg-custom-blue-100",
            width: "col-span-12 sm:col-span-6",
            content: <ResearchFiles />,
          },
        ]
      : []),
    {
      title: "Recent SnapShots",
      description: "View your recently created report snapshots",
      href: "/reports",
      icon: CameraIcon,
      iconForeground: "text-ui-card-icon-color",
      iconBackground: "bg-desert-sand-200",
      width: "col-span-12 sm:col-span-6",
      content: <SnapshotsTableRecentItems />,
    },
  ];

  const fullName = userData?.name && userData?.surname ? `${userData.name} ${userData.surname}` : userData?.username;

  return (
    <div>
      <h1 className="mb-12 mt-4 text-4xl font-bold text-ui-heading-text">
        Welcome back{!!userData && `, ${fullName}`}
      </h1>

      <div className="grid grid-cols-12 gap-12">
        {actions.map(action => (
          <div
            key={action.title}
            className={clsx(
              action.width,
              "group relative overflow-hidden rounded-2xl border-2 border-ivory-mist-300 p-6 shadow-sm transition-shadow duration-300 focus-within:ring-2 focus-within:ring-ivory-mist-900 hover:shadow-sm",
            )}
          >
            <span className="absolute inset-0 rounded-2xl bg-ui-card-background bg-opacity-10" />
            <div className="relative mb-8 flex items-center">
              <span
                className={clsx(
                  action.iconBackground,
                  action.iconForeground,
                  "inline-flex rounded-lg p-3 ring-4 ring-white",
                )}
              >
                <action.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <div className="ml-4">
                <h3 className="text-base font-semibold leading-6 text-ui-heading-text">
                  <a href={action.href} className="focus:outline-none">
                    {action.title}
                  </a>
                </h3>
                <p className="mt-2 text-sm text-ui-paragraph-text">
                  {action.description || "No description provided."}
                </p>
              </div>
            </div>
            <div className="relative mt-4">{action.content}</div>
            <span
              className="pointer-events-none absolute right-6 top-6 text-gray-300 group-hover:text-gray-400"
              aria-hidden="true"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
              </svg>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
